import { Route } from '@angular/router';
import { MsalGuard } from '@azure/msal-angular';
import { initialDataResolver } from 'app/app.resolvers';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { LayoutComponent } from 'app/layout/layout.component';
import { AdminGuard } from './core/auth/guards/admin.guard';

// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [
    // Redirect empty path to
    {
        path: '',
        pathMatch: 'full',
        redirectTo: 'measurement/registry/production',
    },

    // After the user signs in, the sign-in page will redirect the user to the 'signed-in-redirect'
    // path. Below is another redirection for that path to redirect the user to the desired
    // location. This is a small convenience to keep all main routes together here on this file.
    {
        path: 'signed-in-redirect',
        pathMatch: 'full',
        redirectTo: 'measurement/registry/production',
    },

    // Auth routes for guests
    {
        path: '',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'confirmation-required',
                loadChildren: () => import('app/modules/auth/confirmation-required/confirmation-required.routes'),
            },
            {
                path: 'forgot-password',
                loadChildren: () => import('app/modules/auth/forgot-password/forgot-password.routes'),
            },
            {
                path: 'reset-password',
                loadChildren: () => import('app/modules/auth/reset-password/reset-password.routes'),
            },
            {
                path: 'sign-in',
                loadChildren: () => import('app/modules/auth/azure-sign-in/azure-sign-in.routes'),
            },
            {
                path: 'sign-up',
                loadChildren: () => import('app/modules/auth/sign-up/sign-up.routes'),
            },
        ],
    },

    // Auth routes for authenticated users
    {
        path: '',
        canActivate: [MsalGuard],
        canActivateChild: [MsalGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'sign-out',
                loadChildren: () => import('app/modules/auth/sign-out/sign-out.routes'),
            },
            {
                path: 'unlock-session',
                loadChildren: () => import('app/modules/auth/unlock-session/unlock-session.routes'),
            },
        ],
    },

    // Landing routes
    {
        path: '',
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'home',
                loadChildren: () => import('app/modules/landing/home/home.routes'),
            },
        ],
    },

    // Admin routes
    {
        path: '',
        canActivate: [MsalGuard],
        canActivateChild: [MsalGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        children: [
            {
                path: 'dashboard',
                loadChildren: () => import('app/modules/dashboard/dashboard.routes'),
            },
            {
                path: 'measurement/registry/production',
                loadChildren: () => import('app/modules/laboratory/measurement/measurement-production/measurement-production.routes'),
            },
            {
                path: 'measurement/registry/rnd',
                loadChildren: () => import('app/modules/laboratory/measurement/measurement-rnd/measurement-rnd.routes'),
            },

            {
                path: 'measurement/results',
                loadChildren: () => import('app/modules/laboratory/measurement-results/measurement-results.routes'),
            },
            {
                path: 'calorimeter-measurements',
                loadChildren: () => import('app/modules/laboratory/calorimeter-measurement/calorimeter-measurement.routes'),
            },
            {
                path: 'noise-measurements',
                loadChildren: () => import('app/modules/laboratory/noise-measurement/noise-measurement.routes'),
            },
            {
                path: 'eoc-measurements',
                loadChildren: () => import('app/modules/laboratory/eoc-measurement/eoc-measurement.routes'),
            },
            {
                path: 'planning/development-orders/production',
                loadChildren: () =>
                    import('app/modules/laboratory/planning/development-orders/development-orders.routes').then((m) => m.prodRoute),
            },
            {
                path: 'planning/development-orders/rnd',
                loadChildren: () =>
                    import('app/modules/laboratory/planning/development-orders/development-orders.routes').then((m) => m.rndRoute),
            },
            {
                path: 'planning/ats',
                loadChildren: () => import('app/modules/laboratory/planning/ats/planning.routes'),
            },
            {
                path: 'planning/sks',
                loadChildren: () => import('app/modules/laboratory/planning/sks/planning.routes'),
            },
            {
                path: 'reliability',
                loadChildren: () => import('app/modules/laboratory/tribology/tribology.routes'),
            },
            {
                path: 'nav/:id',
                loadChildren: () => import('app/modules/qr-code/qr-code.routes'),
            },
            {
                path: 'admin/factories',
                loadChildren: () => import('app/modules/admin/factories/factories.routes'),
                canActivate: [AdminGuard],
            },
            {
                path: 'admin/measurement-test-type',
                loadChildren: () => import('app/modules/admin/measurement-test-type/measurement-test-type.routes'),
                canActivate: [AdminGuard],
            },
            {
                path: 'admin/test-conditions',
                loadChildren: () => import('app/modules/admin/test-conditions/test-conditions.routes'),
                canActivate: [AdminGuard],
            },
            {
                path: 'admin/users',
                loadChildren: () => import('app/modules/admin/users/users.routes'),
                canActivate: [AdminGuard],
            },
            {
                path: 'admin/refrigerants',
                loadChildren: () => import('app/modules/admin/refrigerants/refrigerants.routes'),
                canActivate: [AdminGuard],
            },
            {
                path: 'admin/performance-tests',
                loadChildren: () => import('app/modules/admin/development-order-performance-tests/performance-test.routes'),
                canActivate: [AdminGuard],
            },
            {
                path: 'admin/reliability-tests',
                loadChildren: () => import('app/modules/admin/development-order-reliability-tests/reliability-test.routes'),
                canActivate: [AdminGuard],
            },
            {
                path: 'admin/approval-tests',
                loadChildren: () => import('app/modules/admin/development-order-approval-tests/development-order-approval-test.routes'),
                canActivate: [AdminGuard],
            },
            {
                path: 'admin/calorimeters',
                loadChildren: () => import('app/modules/admin/calorimeter/calorimeter.routes'),
                canActivate: [AdminGuard],
            },
            {
                path: 'admin/noise-chambers',
                loadChildren: () => import('app/modules/admin/noise-chambers/noise-chambers.routes'),
                canActivate: [AdminGuard],
            },
            // tribologia
            {
                path: 'admin/tribology-wears',
                loadChildren: () => import('app/modules/admin/tribologia/tribologia-wears/tribologia-wears.routes'),
                canActivate: [AdminGuard],
            },
            {
                path: 'admin/tribology-levels',
                loadChildren: () => import('app/modules/admin/tribologia/tribologia-levels/tribologia-levels.routes'),
                canActivate: [AdminGuard],
            },
            {
                path: 'admin/tribology-evaluations',
                loadChildren: () => import('app/modules/admin/tribologia/tribologia-evaluations/tribologia-evaluations.routes'),
                canActivate: [AdminGuard],
            },
            {
                path: 'admin/tribology-components',
                loadChildren: () => import('app/modules/admin/tribologia/tribologia-components/tribologia-components.routes'),
                canActivate: [AdminGuard],
            },
            // end tribologia
            {
                path: 'admin/compressor-codes',
                loadChildren: () => import('app/modules/admin/compressor-codes/compressor-codes.routes'),
                canActivate: [AdminGuard],
            },
            {
                path: 'admin/compressor-types',
                loadChildren: () => import('app/modules/admin/compressor-types/compressor-types.routes'),
                canActivate: [AdminGuard],
            },
            {
                path: 'admin/compressor-data',
                loadChildren: () => import('app/modules/admin/compressor-data/compressor-data.routes'),
                canActivate: [AdminGuard],
            },
            // compressor new endpoints
            {
                path: 'admin/compressor-applications',
                loadChildren: () => import('app/modules/admin/compressor-applications/compressor-applications.routes'),
                canActivate: [AdminGuard],
            },
            {
                path: 'admin/compressor-cooling-systems',
                loadChildren: () => import('app/modules/admin/compressor-cooling-systems/compressor-cooling-systems.routes'),
                canActivate: [AdminGuard],
            },
            {
                path: 'admin/compressor-motor-types',
                loadChildren: () => import('app/modules/admin/compressor-motor-types/compressor-motor-types.routes'),
                canActivate: [AdminGuard],
            },
            {
                path: 'admin/compressor-protector-types',
                loadChildren: () => import('app/modules/admin/compressor-protector-types/compressor-protector-types.routes'),
                canActivate: [AdminGuard],
            },
            {
                path: 'admin/compressor-rotor-types',
                loadChildren: () => import('app/modules/admin/compressor-rotor-types/compressor-rotor-types.routes'),
                canActivate: [AdminGuard],
            },
            {
                path: 'admin/compressor-start-equipments',
                loadChildren: () => import('app/modules/admin/compressor-start-equipments/compressor-start-equipments.routes'),
                canActivate: [AdminGuard],
            },
            {
                path: 'admin/compressor-stator-types',
                loadChildren: () => import('app/modules/admin/compressor-stator-types/compressor-stator-types.routes'),
                canActivate: [AdminGuard],
            },
            {
                path: 'admin/compressor-capacitors',
                loadChildren: () => import('app/modules/admin/compressor-capacitors/compressor-capacitors.routes'),
                canActivate: [AdminGuard],
            },
            //end compressor new endpoints
            {
                path: 'admin/development-order-priorities',
                loadChildren: () => import('app/modules/admin/development-order-priorities/development-order-priorities.routes'),
                canActivate: [AdminGuard],
            },
            {
                path: 'admin/development-order-windings',
                loadChildren: () => import('app/modules/admin/development-order-windings/development-order-windings.routes'),
                canActivate: [AdminGuard],
            },
            {
                path: 'admin/development-order-kit-return-types',
                loadChildren: () =>
                    import('app/modules/admin/development-order-kit-return-types/development-order-kit-return-types.routes'),
                canActivate: [AdminGuard],
            },
            {
                path: 'admin/projects',
                loadChildren: () => import('app/modules/admin/projects/projects.routes'),
                canActivate: [AdminGuard],
            },
            {
                path: 'admin/print-labels',
                loadChildren: () => import('app/modules/admin/label-printer/label-printer.routes'),
                canActivate: [AdminGuard],
            },

            {
                path: 'admin/cataloguevalues',
                loadChildren: () => import('app/modules/admin/catalogue/catalogue.routes'),
                canActivate: [AdminGuard],
            },
        ],
    },
];
